import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './lib/AuthContext'
import Login from './pages/Login'
import Signup from './pages/Signup'
import JoinGroup from './pages/JoinGroup'
import Home from './pages/Home'
import SpinMode from './pages/SpinMode'
import SoloSpin from './pages/SoloSpin'
import AddMovie from './pages/AddMovie'
import Watchlist from './pages/Watchlist'
import Wheel from './pages/Wheel'
import Result from './pages/Result'
import History from './pages/History'
import Rate from './pages/Rate'
import GroupSettings from './pages/GroupSettings'
import CreateGroup from './pages/CreateGroup'
import MovieDetail from './pages/MovieDetail'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import DeleteData from './pages/DeleteData'
import SharedResult from './pages/SharedResult'
import Landing from './pages/Landing'

function Splash() {
  return (
    <div
      className="min-h-[100dvh] flex items-center justify-center"
      style={{ background: 'var(--fp-bg)' }}
    >
      <span className="text-[var(--fp-text-muted)]">Loading...</span>
    </div>
  )
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Splash />
  if (!user) {
    // Save where they were trying to go so we can redirect after login
    const returnTo = location.pathname + location.search
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} />
  }
  return children
}

// Route wrapper for pages that should bounce authed users into the app
// (signup, /join, etc). Distinct from the landing `/`, which renders
// different content per auth state without a redirect flash.
function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Splash />
  if (user) {
    // After login, an explicit returnTo (deep link) always wins.
    const params = new URLSearchParams(location.search)
    const returnTo = params.get('returnTo')
    if (returnTo) return <Navigate to={returnTo} />

    // The entry gate. Skipped when the user has previously asked us to —
    // we send them straight to their last-used mode (default: group).
    let skipGate = false
    let lastMode = null
    try {
      skipGate = localStorage.getItem('flickpick_skip_gate') === 'true'
      lastMode = localStorage.getItem(`fp_last_spin_mode_${user.id}`)
    } catch { /* ignore */ }

    if (skipGate) {
      return <Navigate to={lastMode === 'solo' ? '/solo' : '/'} />
    }
    return <Navigate to="/spin-mode" />
  }
  return children
}

// `/` serves two audiences:
//   - Unauthenticated visitors see the marketing landing page.
//   - Authenticated users see the Home dashboard (matching pre-R4 behaviour).
// While auth state is resolving we show a splash so authed users never get
// a flash of marketing content during OAuth return / initial load.
function RootRoute() {
  const { user, loading } = useAuth()
  if (loading) return <Splash />
  if (!user) return <Landing />
  return <Home />
}

export default function App() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  // Handle OAuth return. The provider redirect lands at '/', which would
  // otherwise skip the entry gate. We use the `fp_oauth_pending` flag
  // (set by Login before redirecting out) to detect a freshly-completed
  // OAuth and route the user through the gate (or straight to their
  // last-used mode if they previously asked us to skip).
  useEffect(() => {
    if (loading || !user) return

    const savedReturn = localStorage.getItem('fp_return_to')
    if (savedReturn) {
      localStorage.removeItem('fp_return_to')
      localStorage.removeItem('fp_oauth_pending')
      navigate(savedReturn, { replace: true })
      return
    }

    if (localStorage.getItem('fp_oauth_pending') === '1') {
      localStorage.removeItem('fp_oauth_pending')
      const skipGate = localStorage.getItem('flickpick_skip_gate') === 'true'
      const lastMode = localStorage.getItem(`fp_last_spin_mode_${user.id}`)
      const target = skipGate
        ? (lastMode === 'solo' ? '/solo' : '/')
        : '/spin-mode'
      navigate(target, { replace: true })
    }
  }, [loading, user])

  return (
    <Routes>
      {/* Public — /login is no longer wrapped so authed users see the
          "already signed in" notice inside the page itself. */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<RedirectIfAuthed><Signup /></RedirectIfAuthed>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-data" element={<DeleteData />} />
      {/* Public shareable result — deliberately listed before /result so it wins the match */}
      <Route path="/result/:slug" element={<SharedResult />} />

      {/* Root — landing for anonymous visitors, dashboard for authed users. */}
      <Route path="/" element={<RootRoute />} />

      {/* Authed app */}
      <Route path="/join/:inviteCode" element={<ProtectedRoute><JoinGroup /></ProtectedRoute>} />
      <Route path="/spin-mode" element={<ProtectedRoute><SpinMode /></ProtectedRoute>} />
      <Route path="/solo" element={<ProtectedRoute><SoloSpin /></ProtectedRoute>} />
      <Route path="/solo/result" element={<ProtectedRoute><Result /></ProtectedRoute>} />
      <Route path="/add" element={<ProtectedRoute><AddMovie /></ProtectedRoute>} />
      <Route path="/movie/:tmdbId" element={<ProtectedRoute><MovieDetail /></ProtectedRoute>} />
      <Route path="/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
      <Route path="/wheel" element={<ProtectedRoute><Wheel /></ProtectedRoute>} />
      <Route path="/result" element={<ProtectedRoute><Result /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/rate" element={<ProtectedRoute><Rate /></ProtectedRoute>} />
      <Route path="/group" element={<ProtectedRoute><GroupSettings /></ProtectedRoute>} />
      <Route path="/create-group" element={<ProtectedRoute><CreateGroup /></ProtectedRoute>} />
      {/* Unknown routes — home for authed, landing for anonymous. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
